import axios from "axios";
import React, { useState } from "react";
import { toast } from "react-hot-toast";
import { Mail, User, Tag, Phone, Send, X, Headphones } from "lucide-react";
import { defaultUrl } from "../../features/actions";
import { globalToken } from "../AdminHome";

interface Message {
  name: string;
  email: string;
  subject: string;
  phone_number?: string;
  message: string;
}

const ContactUs = () => {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState<Message>({
    name: "",
    email: "",
    subject: "",
    message: "",
    phone_number: "",
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    if (
      form.message.length < 4 ||
      form.email.length < 4 ||
      form.name.length < 3 ||
      form.subject.length < 3
    ) {
      toast.error("Please fill in all required fields accurately.");
      return;
    }

    setLoading(true);
    const toastId = toast.loading("Connecting to support...");

    try {
      await axios.post(`${defaultUrl}/contact`, form, {
        headers: { Authorization: "Bearer " + globalToken },
      });

      toast.success("Message sent! Our team will reach out shortly.", {
        id: toastId,
      });
      setForm({
        name: "",
        subject: "",
        message: "",
        phone_number: "",
        email: "",
      });
    } catch (e: any) {
      toast.error("Failed to send message. Please try again later.", {
        id: toastId,
      });
    } finally {
      setLoading(false);
    }
  };

  // Styles matched to the provided UI screenshot
  const labelStyle =
    "text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 block ml-1";
  const inputContainer =
    "relative flex items-center bg-[#F8FAFC] rounded-2xl border-2 border-transparent focus-within:border-blue-500 focus-within:bg-white transition-all px-5 py-4";
  const inputStyle =
    "w-full bg-transparent outline-none text-slate-700 font-bold text-sm placeholder:text-slate-300 placeholder:font-bold";

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 max-w-4xl">
      {/* Header Section */}
      <div className="mb-10">
        <div className="flex items-center gap-5 mb-6">
          <div className="p-4 bg-[#EFF6FF] text-[#3B82F6] rounded-2xl">
            <Headphones size={28} />
          </div>
          <h2 className="text-[36px] font-black text-[#1E293B] tracking-tight">
            Contact Support
          </h2>
        </div>
        <p className="text-slate-500 font-bold text-[15px] max-w-xl leading-relaxed">
          Have a question or need technical assistance? Our support team is here
          to help you 24/7.
        </p>
      </div>

      <div className="space-y-8">
        {/* Row 1: Name and Subject */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="space-y-1">
            <label className={labelStyle}>Full Name</label>
            <div className={inputContainer}>
              <User className="text-slate-300 mr-4" size={18} />
              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="John Doe"
                className={inputStyle}
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className={labelStyle}>Subject</label>
            <div className={inputContainer}>
              <Tag className="text-slate-300 mr-4" size={18} />
              <input
                name="subject"
                value={form.subject}
                onChange={handleChange}
                placeholder="How can we help?"
                className={inputStyle}
              />
            </div>
          </div>
        </div>

        {/* Row 2: Email and Phone */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="space-y-1">
            <label className={labelStyle}>Email Address</label>
            <div className={inputContainer}>
              <Mail className="text-slate-300 mr-4" size={18} />
              <input
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                placeholder="admin@school.com"
                className={inputStyle}
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className={labelStyle}>Phone (Optional)</label>
            <div className={inputContainer}>
              <Phone className="text-slate-300 mr-4" size={18} />
              <input
                name="phone_number"
                value={form.phone_number}
                onChange={handleChange}
                placeholder="+234..."
                className={inputStyle}
              />
            </div>
          </div>
        </div>

        {/* Row 3: Message */}
        <div className="space-y-1">
          <label className={labelStyle}>Detailed Message</label>
          <div className="bg-[#F8FAFC] rounded-[2.5rem] border-2 border-transparent focus-within:border-blue-500 focus-within:bg-white transition-all p-3">
            <textarea
              name="message"
              value={form.message}
              onChange={handleChange}
              placeholder="Please describe your issue in detail..."
              className="w-full min-h-[200px] p-5 bg-transparent outline-none text-slate-700 font-bold text-sm resize-none placeholder:text-slate-300 placeholder:font-bold"
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap items-center gap-5 pt-4">
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="flex-1 md:flex-none px-12 h-16 bg-[#0F172A] text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-2xl hover:scale-[1.02] transition-all flex items-center justify-center gap-3"
          >
            <Send size={16} /> {loading ? "Sending..." : "Send Message"}
          </button>

          <button
            type="button"
            className="flex-1 md:flex-none px-12 h-16 rounded-2xl font-black text-xs uppercase tracking-widest text-slate-400 hover:bg-slate-100 transition-all flex items-center justify-center gap-2"
          >
            <X size={16} /> Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContactUs;
